/**
 * Formatuje cenę na string z walutą PLN.
 * Ceny są już w złotych (skonwertowane w normalizeProduct, zob. lib/store/products.ts).
 */
export const formatPrice = (price: string | number | undefined): string => {
    if (!price) return '0,00 zł';

    const numPrice = typeof price === 'string' ? parseFloat(price) : price;

    if (isNaN(numPrice) || numPrice === 0) return '0,00 zł';

    return new Intl.NumberFormat('pl-PL', {
        style: 'currency',
        currency: 'PLN',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(numPrice);
};

/**
 * Konwertuje cenę z groszy na złote (jako liczba).
 */
export const convertPriceFromGrosz = (price: string | number | undefined): number => {
    if (!price) return 0;
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return 0;
    return numPrice / 100;
};

/**
 * Formatuje cenę z groszy na string z walutą PLN.
 */
export const formatPriceFromGrosz = (price: string | number | undefined): string => {
    const priceInZloty = convertPriceFromGrosz(price);
    return formatPrice(priceInZloty);
};

/**
 * Pobiera cenę z obiektu produktu/wariantu (zwraca w złotych).
 * UWAGA: ceny są już w złotych po normalizacji w polach:
 * - item.price
 * - item.regular_price
 * - item.sale_price
 * - item.prices.price — to NIE JEST przekonwertowane (surowa wartość z API)
 * Dlatego najpierw używamy bezpośrednich pól, a dopiero potem prices.
 */
export const getPrice = (item: any): string => {
    if (!item) return '0';

    if (item.price && item.price !== '0') return item.price;

    if (item.regular_price && item.regular_price !== '0') return item.regular_price;

    // Ostatnia deska ratunku — prices.price (może być w groszach!)
    if (item.prices?.price) {
        const numPrice = parseFloat(item.prices.price);
        if (Number.isInteger(numPrice) && numPrice > 100) {
            return (numPrice / 100).toString();
        }
        return item.prices.price;
    }

    return '0';
};

/**
 * Pobiera cenę regularną (w złotych).
 */
export const getRegularPrice = (item: any): string => {
    if (!item) return '';

    if (item.regular_price) return item.regular_price;

    if (item.prices?.regular_price) {
        const numPrice = parseFloat(item.prices.regular_price);
        if (Number.isInteger(numPrice) && numPrice > 100) {
            return (numPrice / 100).toString();
        }
        return item.prices.regular_price;
    }

    return '';
};

/**
 * Pobiera cenę promocyjną (w złotych).
 */
export const getSalePrice = (item: any): string => {
    if (!item) return '';

    if (item.sale_price) return item.sale_price;

    if (item.prices?.sale_price) {
        const numPrice = parseFloat(item.prices.sale_price);
        if (Number.isInteger(numPrice) && numPrice > 100) {
            return (numPrice / 100).toString();
        }
        return item.prices.sale_price;
    }

    return '';
};

/**
 * Sprawdza, czy produkt/wariant jest na promocji.
 */
export const isProductOnSale = (item: any): boolean => {
    if (!item) return false;

    if (item.prices?.on_sale) return true;

    const salePrice = getSalePrice(item);
    const regularPrice = getRegularPrice(item);

    if (!salePrice || !regularPrice) return false;

    const sale = parseFloat(salePrice);
    const regular = parseFloat(regularPrice);

    return sale > 0 && regular > 0 && sale < regular;
};
